import React, { useMemo } from 'react'

import { twMerge } from 'tailwind-merge'

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/common/TooltipV3'
import { ColorIndicator } from '@/components/tags/ColorIndicator'

import { compareTagsByPosition } from '@/helpers/tags'

import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useTags } from '@/hooks/tags/useTags'

import type { UUID } from '@/types/Id'

interface InlineTagSummaryProps {
  tagIds: UUID[]
  className?: string
}

const stopPropagation = (event: React.SyntheticEvent<HTMLElement>) => {
  event.stopPropagation()
}

export const InlineTagSummary: React.FC<InlineTagSummaryProps> = ({
  tagIds,
  className,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const { data: tags } = useTags({ orgId: activeOrgId, tagIds, tagType: null })

  const sortedTags = useMemo(() => {
    if (!tags) {
      return []
    }

    return [...tags].sort(compareTagsByPosition)
  }, [tags])

  if (sortedTags.length === 0) {
    return null
  }

  const primaryTag = sortedTags[0]
  const remainingTagCount = sortedTags.length - 1

  return (
    <TooltipProvider delayDuration={250}>
      <Tooltip>
        <TooltipTrigger asChild={true}>
          <button
            type="button"
            data-selection-ignore="true"
            onClick={stopPropagation}
            className={twMerge(
              'mt-0.5 inline-flex max-w-full items-center gap-1.5 overflow-hidden rounded-sm text-left text-xs text-primary-500 transition-colors hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-200',
              className,
            )}
          >
            <div className="flex shrink-0 items-center gap-1">
              {sortedTags.map((tag) => (
                <ColorIndicator
                  key={tag.id}
                  color={tag.color}
                  className="h-2.5 w-2.5"
                />
              ))}
            </div>
            <span className="truncate leading-4">{primaryTag.name}</span>
            {remainingTagCount > 0 ? (
              <>
                <span className="shrink-0 text-primary-300 dark:text-primary-600">
                  ·
                </span>
                <span className="shrink-0 leading-4">
                  +{remainingTagCount} more
                </span>
              </>
            ) : null}
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="left"
          align="center"
          inverseColors={true}
          className="max-w-[240px] px-2.5 py-2"
        >
          <div className="flex min-w-[160px] max-w-[220px] flex-col gap-1.5">
            {sortedTags.map((tag) => (
              <div
                key={tag.id}
                className="flex min-w-0 items-center gap-2 text-left text-[13px] text-primary-900 dark:text-primary-100"
              >
                <ColorIndicator
                  color={tag.color}
                  className="h-2.5 w-2.5 shrink-0"
                />
                <span className="truncate">{tag.name}</span>
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

InlineTagSummary.displayName = 'InlineTagSummary'
